import Vue from 'vue';
import Router, { NavigationFailureType } from 'vue-router';
import Meta from 'vue-meta'

Vue.use(Router);
Vue.use(Meta, {
    ssrAppId: 1 // https://vue-meta.nuxtjs.org/guide/caveats.html#duplicated-tags-after-hydration-with-ssr
});

import { countryCode, featureContributionsAvailable, isAuthenticated } from "../store/getters";

/**
 * Helper function intended to be used with `Array.prototype.filter` to return
 * only unique elements in the array.
 *
 * This only works if the array contains primitive types, i.e. types whose
 * equality can be compared with `===`.
 *
 * @example
 *     // Returns `["foo", "bar"]`.
 *     ["foo", "bar", "foo"].filter(unique)
 */
function unique(item, i, arr) {
    return arr.indexOf(item) === i;
}

/**
 * Get the path pattern for a route, in the configured language.
 *
 * @param {object} i18n Instance of `vue-i18n` library.
 * @param {string} routeName The name of the route for which we want to get the
 *     translated route pattern.
 * @returns {string} The path pattern in the configured language.
 */
function getPreferredRoutePath(i18n, routeName) {
    const key = `routes.${routeName}`;

    const routePath = i18n.t(key);
    if (routePath === key) {
        return "";
    }

    return routePath;
}

/**
 * Get all possible aliases, in all configured languages, for the given route
 * name.
 *
 * @param {object} i18n Instance of `vue-i18n` library.
 * @param {string} routeName The name of the route for which we want to get translations.
 * @returns {Array} All possible route patterns.
 */
function getRouteAliases(i18n, routeName) {
    return Object
        .entries(i18n.messages)
        .map(([language, literals]) => {
            return (literals?.routes || {})[routeName];
        })
        .filter(route => !!route);
}

/**
 * Returns the routes that will be
 *
 * @param {object} functions Functions that will be used during the creation of
 *     the array.
 * @returns {Array} Routes.
 */
const routes = ({
    translateRoute,
    featureContributionsAvailable,
    locationCountry,
    isAuthenticated,
}) => {
    const originalRoutes = [
        {
            path: '/lista-de-nacimiento', component: () => import(/* webpackChunkName: "home" */  '../modules/home/HomePageES'), name: 'home', meta: { footerLang: 'es-ES', headerLang: 'es-ES', headerType: 'baby' },
            beforeEnter: (to, from, next) => {
                if (isAuthenticated()) {
                    next({ name: 'owner-list' });
                }
                else if (locationCountry() === 'FR' || locationCountry() == 'CH' || locationCountry() == 'BE' || locationCountry() == 'LU') {
                    next({ name: 'HomeFR' });
                }
                else if (locationCountry() === 'IT') {
                    next({ name: 'HomeIT' });
                }
                else if (locationCountry() === 'BR') {
                    next({ name: 'HomeBR' });
                }
                else next();
            }
        },
        {
            path: '/mesa-de-regalos', component: () => import(/* webpackChunkName: "home" */  '../modules/home/HomePageINT'), name: 'HomeINT',
            beforeEnter: (to, from, next) => {
                if (isAuthenticated()) {
                    next({ name: 'owner-list' });
                }
                else {
                    next();
                }
            }
        },
        {
            path: '/liste-de-naissance', component: () => import(/* webpackChunkName: "home" */  '../modules/home/HomePageFR'), name: 'HomeFR', meta: { footerLang: 'fr-FR', headerLang: 'fr-FR' },
            beforeEnter: (to, from, next) => {
                if (isAuthenticated()) {
                    next({ name: 'owner-list' });
                }
                else {
                    next();
                }
            }
        },
        {
            path: '/lista-di-nascita', component: () => import(/* webpackChunkName: "home" */  '../modules/home/HomePageIT'), name: 'HomeIT', meta: { footerLang: 'it-IT', headerLang: 'it-IT' },
            beforeEnter: (to, from, next) => {
                if (isAuthenticated()) {
                    next({ name: 'owner-list' });
                }
                else {
                    next();
                }
            }
        },
        {
            path: '/lista-de-nascimento', component: () => import(/* webpackChunkName: "home" */  '../modules/home/HomePageBR'), name: 'HomeBR', meta: { footerLang: 'pt-BR', headerLang: 'pt-BR' },
            beforeEnter: (to, from, next) => {
                if (isAuthenticated()) {
                    next({ name: 'owner-list' });
                }
                else {
                    next();
                }
            }
        },
        // { path: '/es', component: () => import(/* webpackChunkName: "home" */  '../modules/home/HomePageES'), name: 'HomeES', meta: {footerLang: 'es-ES', headerLang: 'es-ES'} },

        { 
            path: '/', component: () => import(/* webpackChunkName: "home-multilista" */ '../modules/home-multilista/HomeMultilista.vue'), name: 'home-multilista',
            beforeEnter: (to, from, next) => {
                if (isAuthenticated()) {
                    next({ name: 'owner-list' });
                }
                else if (locationCountry() === 'FR' || locationCountry() == 'CH' || locationCountry() == 'BE' || locationCountry() == 'LU') {
                    next({ name: 'home-multilista-fr', query: to.query });
                }
                else if (locationCountry() === 'IT') {
                    //preserve query params when redirecting to italian home multilista
                    next({ name: 'home-multilista-it', query: to.query });
                }
                else if (locationCountry() === 'GB' || locationCountry() === 'AU') {
                    next({ name: 'home-multilista-en', query: to.query });
                }
                else if (locationCountry() === 'DE') {
                    next({ name: 'home-multilista-de', query: to.query });
                }
                else if (locationCountry() === 'NL') {
                    next({ name: 'home-multilista-nl', query: to.query });
                }
                else if (locationCountry() === 'BR' || locationCountry() === 'PT') {
                    next({ name: 'home-multilista-pt', query: to.query });
                }
                else next();
            }
        },
        { 
            path: '/fr', component: () => import(/* webpackChunkName: "home-multilista" */ '../modules/home-multilista/HomeWishlistFR.vue'), name: 'home-multilista-fr', meta: { footerLang: 'fr-FR', headerLang: 'fr-FR' },
            beforeEnter: (to, from, next) => {
                if (isAuthenticated()) {
                    next({ name: 'owner-list' });
                }
                else next();
            } 
        },
        { 
            path: '/it', component: () => import(/* webpackChunkName: "home-multilista" */ '../modules/home-multilista/HomeWishlistIT.vue'), name: 'home-multilista-it', meta: { footerLang: 'it-IT', headerLang: 'it-IT' },
            beforeEnter: (to, from, next) => {
                if (isAuthenticated()) {
                    next({ name: 'owner-list' });
                }
                else next();
            }
        },
        { 
            path: '/en', component: () => import(/* webpackChunkName: "home-multilista" */ '../modules/home-multilista/HomeWishlistEN.vue'), name: 'home-multilista-en', meta: { footerLang: 'en-GB', headerLang: 'en-GB' },
            beforeEnter: (to, from, next) => {
                if (isAuthenticated()) {
                    next({ name: 'owner-list' });
                }
                else next();
            }
        },
        { 
            path: '/de', component: () => import(/* webpackChunkName: "home-multilista" */ '../modules/home-multilista/HomeWishlistDE.vue'), name: 'home-multilista-de', meta: { footerLang: 'de-DE', headerLang: 'de-DE' },
            beforeEnter: (to, from, next) => {
                if (isAuthenticated()) {
                    next({ name: 'owner-list' });
                }
                else next();
            }
        },
        {
            path: '/nl', component: () => import(/* webpackChunkName: "home-multilista" */ '../modules/home-multilista/HomeWishlistNL.vue'), name: 'home-multilista-nl', meta: { footerLang: 'nl-NL', headerLang: 'nl-NL' },
            beforeEnter: (to, from, next) => {
                if (isAuthenticated()) {
                    next({ name: 'owner-list' });
                }
                else next();
            }
        },
        {
            path: '/pt', component: () => import(/* webpackChunkName: "home-multilista" */ '../modules/home-multilista/HomeWishlistBR.vue'), name: 'home-multilista-pt', meta: { footerLang: 'pt-BR', headerLang: 'pt-BR' },
            beforeEnter: (to, from, next) => {
                if (isAuthenticated()) {
                    next({ name: 'owner-list' });
                }
                else next();
            }
        },


        { path: '/contributions', component: () => import(/* webpackChunkName: "contributions" */ '../modules/hucha/HuchaAlt.vue'), name: 'contributions' },
        { path: '/about', component: () => import(/* webpackChunkName: "about" */ '../modules/about/index.vue'), name: 'about' },
        { path: '/login', component: () => import(/* webpackChunkName: "login" */ '../modules/login/LoginPage'), name: 'login' },
        { path: '/login-app', component: () => import(/* webpackChunkName: "login-app" */ '../modules/login/LoginAppBridge'), name: 'login-app' },
        { path: '/connexion', component: () => import(/* webpackChunkName: "acceder" */ '../modules/login/LoginPage'), name: 'connexion', meta: { footerLang: 'fr-FR', headerLang: 'fr-FR', lang: 'fr-FR' } },
        { path: '/accesso', component: () => import(/* webpackChunkName: "acceder" */ '../modules/login/LoginPage'), name: 'accesso', meta: { footerLang: 'it-IT', headerLang: 'it-IT', lang: 'it-IT' } },
        { path: '/signin', component: () => import(/* webpackChunkName: "acceder" */ '../modules/login/LoginPage'), name: 'accesso', meta: { footerLang: 'en-GB', headerLang: 'en-GB', lang: 'en-GB' } },
        { path: '/anmelden', component: () => import(/* webpackChunkName: "anmelden" */ '../modules/login/LoginPage'), name: 'login-de', meta: { footerLang: 'de-DE', headerLang: 'de-DE', lang: 'de-DE' } },
        { path: '/inloggen', component: () => import(/* webpackChunkName: "inloggen" */ '../modules/login/LoginPage'), name: 'login-nl', meta: { footerLang: 'nl-NL', headerLang: 'nl-NL', lang: 'nl-NL' } },
        { path: '/entrar', component: () => import(/* webpackChunkName: "entrar" */ '../modules/login/LoginPage'), name: 'login-pt', meta: { footerLang: 'pt-BR', headerLang: 'pt-BR', lang: 'pt-BR' } },
        { path: '/signup', component: () => import(/* webpackChunkName: "signup" */ '../modules/login/SignUpPage'), name: 'signup', meta: { footer: false } },
        { path: '/signup-landing', component: () => import(/* webpackChunkName: "signup" */ '../modules/login/SignUpPageLanding'), name: 'signup-landing', meta: { header: false, footer: false } },
        { path: '/creez-votre-liste', component: () => import(/* webpackChunkName: "creez-votre-liste" */ '../modules/login/SignUpPage'), name: 'creez-votre-liste', meta: { footer: false, footerLang: 'fr-FR', headerLang: 'fr-FR', lang: 'fr-FR' } },
        { path: '/registrazione', component: () => import(/* webpackChunkName: "registrazione" */ '../modules/login/SignUpPage'), name: 'registrazione', meta: { footer: false, footerLang: 'it-IT', headerLang: 'it-IT', lang: 'it-IT' } },
        { path: '/join', component: () => import(/* webpackChunkName: "registrazione" */ '../modules/login/SignUpPage'), name: 'registrazione', meta: { footer: false, footerLang: 'en-GB', headerLang: 'en-GB', lang: 'en-GB' } },
        { path: '/registrieren', component: () => import(/* webpackChunkName: "registrieren" */ '../modules/login/SignUpPage'), name: 'signup-de', meta: { footer: false, footerLang: 'de-DE', headerLang: 'de-DE', lang: 'de-DE' } },
        { path: '/registreren', component: () => import(/* webpackChunkName: "aanmelden" */ '../modules/login/SignUpPage'), name: 'signup-nl', meta: { footer: false, footerLang: 'nl-NL', headerLang: 'nl-NL', lang: 'nl-NL' } },
        { path: '/cadastro', component: () => import(/* webpackChunkName: "cadastro" */ '../modules/login/SignUpPage'), name: 'signup-pt', meta: { footer: false, footerLang: 'pt-BR', headerLang: 'pt-BR', lang: 'pt-BR' } },
        { path: '/recoverpwd', component: () => import(/* webpackChunkName: "recoverpwd" */ '../modules/login/RecoverPage'), name: 'recoverpwd' },
        { path: '/restablecer-pwd', component: () => import(/* webpackChunkName: "restablecer-pwd" */ '../modules/login/ResetPassword'), name: 'resetpwd', meta: { header: false, footer: false } },
        { path: '/invitacion', component: () => import(/* webpackChunkName: "invitacion" */ '../modules/login/Invitacion'), name: 'invitacion' },
        { path: '/cookies', component: () => import(/* webpackChunkName: "cookies" */ '../modules/login/CookiesPage'), name: 'cookies' },
        { path: '/eliminacion-cuenta', component: () => import(/* webpackChunkName: "eliminacionCuenta" */ '../modules/pages/eliminarCuenta'), name: 'eliminarcuenta' },
        { path: '/politica-privacidad', component: () => import(/* webpackChunkName: "privacidad" */ '../modules/login/PrivacityPage'), name: 'privacity' },
        { path: '/politique-de-confidentialite', component: () => import(/* webpackChunkName: "privacidad" */ '../modules/login/PrivacityPage'), name: 'confidencialite', meta: { footerLang: 'fr-FR', headerLang: 'fr-FR', lang: 'fr-FR' } },
        { path: '/politica-sulla-privacy', component: () => import(/* webpackChunkName: "privacidad" */ '../modules/login/PrivacityPage'), name: 'politica-sulla-privacy', meta: { footerLang: 'it-IT', headerLang: 'it-IT', lang: 'it-IT' } },
        { path: '/privacy-policy', component: () => import(/* webpackChunkName: "privacidad" */ '../modules/login/PrivacityPage'), name: 'privacy-policy', meta: { footerLang: 'en-GB', headerLang: 'en-GB', lang: 'en-GB' } },
        { path: "/datenschutzbestimmungen" , component: () => import(/* webpackChunkName: "privacidad" */ '../modules/login/PrivacityPage'), name: 'datenschutzbestimmungen', meta: { footerLang: 'de-DE', headerLang: 'de-DE', lang: 'de-DE' } },
        { path: "/privacybeleid" , component: () => import(/* webpackChunkName: "privacidad" */ '../modules/login/PrivacityPage'), name: 'privacybeleid', meta: { footerLang: 'nl-NL', headerLang: 'nl-NL', lang: 'nl-NL' } },
        { path: "/politica-de-privacidade", component: () => import(/* webpackChunkName: "privacidad" */ '../modules/login/PrivacityPage'), name: 'politica-de-privacidade', meta: { footerLang: 'pt-BR', headerLang: 'pt-BR', lang: 'pt-BR' } },
        { path: '/legal', component: () => import(/* webpackChunkName: "legal" */ '../modules/login/LegalPage'), name: 'legal' },
        { path: '/hucha-legal', component: () => import(/* webpackChunkName: "legal" */ '../modules/pages/PbTerms'), name: 'pbterms', meta : {footerLang: 'es-ES', headerLang: 'es-ES', lang: 'es-ES'} },
        { path: '/cagnotte-legal', component: () => import(/* webpackChunkName: "legal" */ '../modules/pages/PbTerms'), name: 'pbterms', meta : {footerLang: 'fr-FR', headerLang: 'fr-FR', lang: 'fr-FR'} },
        { path: '/salvadanaio-legal', component: () => import(/* webpackChunkName: "legal" */ '../modules/pages/PbTerms'), name: 'pbterms', meta : {footerLang: 'it-IT', headerLang: 'it-IT', lang: 'it-IT'} },
        { path: '/hucha-legal-aportador', component: () => import(/* webpackChunkName: "legal" */ '../modules/pages/PbContributorTerms'), name: 'pbcontributorterms', meta : {footerLang: 'es-ES', headerLang: 'es-ES', lang: 'es-ES'} },
        { path: '/cagnotte-legal-contributeurs', component: () => import(/* webpackChunkName: "legal" */ '../modules/pages/PbContributorTerms'), name: 'pbcontributorterms', meta : {footerLang: 'fr-FR', headerLang: 'fr-FR', lang: 'fr-FR'} },
        { path: '/salvadanaio-legal-contributori', component: () => import(/* webpackChunkName: "legal" */ '../modules/pages/PbContributorTerms'), name: 'pbcontributorterms', meta : {footerLang: 'it-IT', headerLang: 'it-IT', lang: 'it-IT'} },
        { path: '/help-contributions', component: () => import(/* webpackChunkName: "help-contributions" */ '../modules/pages/WebSiteContributions'), name: 'websitecontributions' },
        { path: '/mentions-legales', component: () => import(/* webpackChunkName: "legal" */ '../modules/login/LegalPage'), name: 'mentions-legal', meta: { footerLang: 'fr-FR', headerLang: 'fr-FR', lang: 'fr-FR' } },
        { path: '/avviso-legale', component: () => import(/* webpackChunkName: "legal" */ '../modules/login/LegalPage'), name: 'avviso-legale', meta: { footerLang: 'it-IT', headerLang: 'it-IT', lang: 'it-IT' } },
        { path: '/legal-notice', component: () => import(/* webpackChunkName: "legal" */ '../modules/login/LegalPage'), name: 'legal-notice', meta: { footerLang: 'en-GB', headerLang: 'en-GB', lang: 'en-GB' } },
        { path: "/rechtliches" , component: () => import(/* webpackChunkName: "legal" */ '../modules/login/LegalPage'), name: 'rechtliches', meta: { footerLang: 'de-DE', headerLang: 'de-DE', lang: 'de-DE' } },
        { path: "/juridisch", component: () => import(/* webpackChunkName: "legal" */ '../modules/login/LegalPage'), name: 'juridisch', meta: { footerLang: 'nl-NL', headerLang: 'nl-NL', lang: 'nl-NL' } },
        { path: "/termos-legais" , component: () => import(/* webpackChunkName: "legal" */ '../modules/login/LegalPage'), name: 'legal-pt', meta: { footerLang: 'pt-BR', headerLang: 'pt-BR', lang: 'pt-BR' } },
        { path: '/afiliacion-y-publicidad', component: () => import(/* webpackChunkName: "afiliacion" */ '../modules/login/AdvertisingAndAffiliationPage'), name: 'advertisingandaffiliation' },
        { path: '/affiliation-et-publicite', component: () => import(/* webpackChunkName: "afiliacion" */ '../modules/login/AdvertisingAndAffiliationPage'), name: 'affiliation-et-publicite', meta: { footerLang: 'fr-FR', headerLang: 'fr-FR', lang: 'fr-FR' } },
        { path: '/affiliazione-e-pubblicita', component: () => import(/* webpackChunkName: "afiliacion" */ '../modules/login/AdvertisingAndAffiliationPage'), name: 'affiliazione-e-pubblicita', meta: { footerLang: 'it-IT', headerLang: 'it-IT', lang: 'it-IT' } },
        { path: '/affiliation-and-advertising', component: () => import(/* webpackChunkName: "afiliacion" */ '../modules/login/AdvertisingAndAffiliationPage'), name: 'affiliation-and-advertising', meta: { footerLang: 'en-GB', headerLang: 'en-GB', lang: 'en-GB' } },
        { path: "/affiliation-und-werbung" , component: () => import(/* webpackChunkName: "afiliacion" */ '../modules/login/AdvertisingAndAffiliationPage'), name: 'affiliation-und-werbung', meta: { footerLang: 'de-DE', headerLang: 'de-DE', lang: 'de-DE' } },
        { path: "/affiliatie-en-reclame", component: () => import(/* webpackChunkName: "afiliacion" */ '../modules/login/AdvertisingAndAffiliationPage'), name: 'affiliatie-en-reclame', meta: { footerLang: 'nl-NL', headerLang: 'nl-NL', lang: 'nl-NL' } },
        { path: "/afiliacao-e-publicidade", component: () => import(/* webpackChunkName: "afiliacion" */ '../modules/login/AdvertisingAndAffiliationPage'), name: 'afiliacao-e-publicidade', meta: { footerLang: 'pt-BR', headerLang: 'pt-BR', lang: 'pt-BR' } },
        { path: '/mapa-web', component: () => import(/* webpackChunkName: "afiliacion" */ '../modules/pages/mapaWeb'), name: 'mapaweb' },
        { path: '/devoluciones', redirect: { name: 'faq', hash: '#nav-devoluciones' } },
        { path: '/envios', redirect: { name: 'faq', hash: '#nav-envios' } },
        { path: '/programa-de-recompensas', redirect: { name: 'faq', hash: '#nav-programa-recompensas' } },
        { path: '/preguntas-frecuentes', component: () => import(/* webpackChunkName: "faq" */ '../modules/pages/ecommerceFaq.vue'), name: 'faq' },
        { path: '/foire-aux-questions', component: () => import(/* webpackChunkName: "foire-aux-questions" */ '../modules/pages/ecommerceFaq.vue'), name: 'foire-aux-questions', meta: { footerLang: 'fr-FR', headerLang: 'fr-FR', lang: 'fr-FR' } },
        { path: '/domande-frequenti', component: () => import(/* webpackChunkName: "foire-aux-questions" */ '../modules/pages/ecommerceFaq.vue'), name: 'domande-frequenti', meta: { footerLang: 'it-IT', headerLang: 'it-IT', lang: 'it-IT' } },
        { path: '/faq', component: () => import(/* webpackChunkName: "foire-aux-questions" */ '../modules/pages/ecommerceFaq.vue'), name: 'faq', meta: { footerLang: 'en-GB', headerLang: 'en-GB', lang: 'en-GB' } },
        { path: "/haufig-gestellte-fragen", component: () => import(/* webpackChunkName: "haufig-gestellte-fragen" */ '../modules/pages/ecommerceFaq.vue'), name: 'haufig-gestellte-fragen', meta: { footerLang: 'de-DE', headerLang: 'de-DE', lang: 'de-DE' } },
        { path: "/veelgestelde-vragen", component: () => import(/* webpackChunkName: "veelgestelde-vragen" */ '../modules/pages/ecommerceFaq.vue'), name: 'veelgestelde-vragen', meta: { footerLang: 'nl-NL', headerLang: 'nl-NL', lang: 'nl-NL' } },
        { path: "/perguntas-frequentes", component: () => import(/* webpackChunkName: "perguntas-frequentes" */ '../modules/pages/ecommerceFaq.vue'), name: 'perguntas-frequentes', meta: { footerLang: 'pt-BR', headerLang: 'pt-BR', lang: 'pt-BR' } },
        { path: '/sobre-nosotros', component: () => import(/* webpackChunkName: "nosotros" */ '../modules/pages/pagesNosotros.vue'), name: 'nosotros' },
        { path: '/qui-sommes-nous', component: () => import(/* webpackChunkName: "nosotros" */ '../modules/pages/pagesNosotros.vue'), name: 'nous', meta: { footerLang: 'fr-FR', headerLang: 'fr-FR', lang: 'fr-FR' } },
        { path: '/chi-siamo', component: () => import(/* webpackChunkName: "nosotros" */ '../modules/pages/pagesNosotros.vue'), name: 'chi-siamo', meta: { footerLang: 'it-IT', headerLang: 'it-IT', lang: 'it-IT' } },
        { path: '/about-us', component: () => import(/* webpackChunkName: "nosotros" */ '../modules/pages/pagesNosotros.vue'), name: 'about-us', meta: { footerLang: 'en-GB', headerLang: 'en-GB', lang: 'en-GB' } },
        { path: "/uber-uns", component: () => import(/* webpackChunkName: "uber-uns" */ '../modules/pages/pagesNosotros.vue'), name: 'uber-uns', meta: { footerLang: 'de-DE', headerLang: 'de-DE', lang: 'de-DE' } },
        { path: "/over-ons", component: () => import(/* webpackChunkName: "over-ons" */ '../modules/pages/pagesNosotros.vue'), name: 'over-ons', meta: { footerLang: 'nl-NL', headerLang: 'nl-NL', lang: 'nl-NL' } },
        { path: "/sobre-nos" , component: () => import(/* webpackChunkName: "sobre-nos" */ '../modules/pages/pagesNosotros.vue'), name: 'sobre-nos', meta: { footerLang: 'pt-BR', headerLang: 'pt-BR', lang: 'pt-BR' } },
        { path: '/sobre-hellobb', redirect: { name: 'faq', hash: '#nav-hellobb' } },
        { path: '/ayuda-configuracion', redirect: { name: 'faq', hash: '#nav-configuracion' } },
        { path: '/ayuda-fondos', redirect: { name: 'faq', hash: '#nav-fondos' } },
        { path: '/compartiendo-y-gestionado-mi-lista', redirect: { name: 'faq', hash: '#nav-regalo' } },
        { path: '/haciendo-mi-lista', redirect: { name: 'faq', hash: '#nav-lista' } },
        { path: '/hucha-de-aportaciones', redirect: { name: 'faq', hash: '#nav-hucha' } },

        { path: '/configuracion', component: () => import(/* webpackChunkName: "configuration" */ '../modules/configuration/ConfigurationPage'), name: 'configuration', meta: { private: true } },
        { path: '/configuracion/mi-lista', component: () => import(/* webpackChunkName: "configuration-mylist" */ '../modules/configuration/ConfigurationMyListPage'), name: 'configuration-mylist', meta: { private: true } },
        // {
        //     path: '/configuracion/mi-hucha',
        //     component: () => featureContributionsAvailable()
        //         ? import(/* webpackChunkName: "configuration-mypiggybank" */ '../modules/configuration/ConfigurationMyPiggyBankPage')
        //         : import(/* webpackChunkName: "not-available-international" */ '../modules/NotAvailableInternationalPage.vue'),
        //     name: 'configuration-mypiggybank',
        //     meta: { private: true },
        // },
        {
            path: '/configuracion/mi-hucha',
            component: () => featureContributionsAvailable()
                ? import(/* webpackChunkName: "configuration-mypiggybank" */ '../modules/configuration/ConfigurationMyPiggybank')
                : import(/* webpackChunkName: "not-available-international" */ '../modules/NotAvailableInternationalPage.vue'),
            name: 'configuration-mypiggybank',
            meta: { private: true },
        },

        { path: '/mi-lista', component: () => import(/* webpackChunkName: "owner-list" */ '../modules/ownerlist/OwnerListPage.vue'), name: 'owner-list', meta: { private: true, footer: 'FooterList' } },
        { path: '/listas', component: () => import(/* webpackChunkName: "lists" */ '../modules/ownerlist/MultilistPageLists.vue'), name: 'lists', meta: { private: true, footer: 'FooterList' } },

        // NOTE: Some components depend on the names of the constructor route
        // names to show or hide HTML elements. If constructor routes are
        // modified, added, or removed, then those components might need to be
        // updated.
        //
        // For more information, run `git blame` on the lines of this comment
        // and take a look at the other files modified in the same commit.
        { path: '/constructor', component: () => import(/* webpackChunkName: "list-constructor" */ '../modules/constructor/ConstructorSelector.vue'), name: 'list-constructor', meta: { private: true, footer: 'FooterList' } },
        { path: '/nuevo-producto', component: () => import(/* webpackChunkName: "add-product" */ '../modules/constructor/AddProductPage.vue'), name: 'add-product', meta: { private: true, footer: 'FooterList' } },
        { path: '/constructor/:brand/:id', component: () => import(/* webpackChunkName: "constructor-product" */ '../modules/constructor/ConstructorProductPage.vue'), name: 'constructor-product', meta: { private: true, header: false, footer: 'FooterList' } },
        { path: '/constructor/blog/posts/:slug', component: () => import(/* webpackChunkName: "ConstructorBlogArticle" */ '../modules/constructor/BlogArticle.vue'), name: 'ConstructorBlogArticle', meta: { private: true, header: false, footer: 'FooterList' } },
        { path: '/add-product-extension', component: () => import(/* webpackChunkName: "add-product-external" */ '../modules/constructor/AddProductExtension.vue'), name: 'add-product-extension', meta: { private: false, header: false, footer: false } },

        { path: '/external-ecommerce/modal', component: () => import(/* webpackChunkName: "external-ecommerce" */ '../modules/external-ecommerce/ModalIframe.vue'), name: 'external-ecommerce-modal-iframe', meta: { private: false, header: false, footer: false, iframe: true } },
        { path: '/external-ecommerce/button', component: () => import(/* webpackChunkName: "external-ecommerce" */ '../modules/external-ecommerce/ButtonIframe.vue'), name: 'external-ecommerce-button-iframe', meta: { private: false, header: false, footer: false, iframe: true } },
        { path: '/external-ecommerce/social-login-success', component: () => import(/* webpackChunkName: "external-ecommerce" */ '../modules/external-ecommerce/SocialLoginSuccess'), name: 'external-ecommerce-social-login-success' },
        { path: '/external-ecommerce/list', component: () => import(/* webpackChunkName: "external-ecommerce" */ '../modules/external-ecommerce/ListPage.vue'), name: 'external-ecommerce-list', meta: { private: true, header: false, footer: false, iframe: true } },
        { path: '/external-ecommerce/login', component: () => import(/* webpackChunkName: "external-ecommerce" */ '../modules/external-ecommerce/components/LoginModal.vue'), name: 'external-ecommerce-login-iframe', meta: { private: false, header: false, footer: false, iframe: true } },
        { path: '/external-ecommerce/signup', component: () => import(/* webpackChunkName: "external-ecommerce" */ '../modules/external-ecommerce/components/SignupModal.vue'), name: 'external-ecommerce-signup-iframe', meta: { private: false, header: false, footer: false, iframe: true } },

        // id -> En el cas dels productes de catàleg serà el listProductId, per poder treballar amb el tema de variants/opcions (el id de producte en variants es compartit). Cas de productes free serà el id de producte
        { path: '/mi-lista/comprar/:productType/:id', component: () => import(/* webpackChunkName: "owner-list-shop-product" */ '../modules/ownerlist/ListProductPageComprar.vue'), name: 'owner-list-shop-product', meta: { private: true, footer: 'FooterList' } },
        { path: '/mi-lista/comprar/list/:listid/:productType/:id', component: () => import(/* webpackChunkName: "owner-list-shop-product" */ '../modules/ownerlist/ListProductPageComprar.vue'), name: 'owner-list-shop-product-other-list', meta: { private: true, footer: 'FooterList' } },
        // id -> En el cas dels productes de catàleg serà el listProductId, per poder treballar amb el tema de variants/opcions (el id de producte en variants es compartit). Cas de productes free serà el id de producte
        { path: '/mi-lista/product/:productType/:id', component: () => import(/* webpackChunkName: "owner-list-edit-product" */ '../modules/ownerlist/EditProductPage.vue'), name: 'owner-list-edit-product', meta: { private: true, footer: 'FooterList' } },
        // Routes per a Mi-Hucha
        {
            path: '/mi-hucha/crear',
            component: () => import(/* webpackChunkName: "mi-hucha-crear" */ '../modules/hucha/HuchaCrear.vue'),
            name: 'hucha-crear',
            meta: { private: true, footer: 'FooterList' },
            beforeEnter: (to, from, next) => {
                if (featureContributionsAvailable()) {
                    next();
                } else {
                    next({ name: 'not-available-international' });
                }
            }
        },
        { path: '/not-available-international', component: () => import(/* webpackChunkName: "not-available-international" */ '../modules/NotAvailableInternationalPage.vue'), name: 'not-available-international', meta: { private: false } },
        {
            path: '/hucha-digital',
            component: () => import(/* webpackChunkName: "mi-hucha-crear" */ '../modules/hucha/HuchaCrear.vue'),
            name: 'hucha-de-nacimiento',
            meta: { private: false },
            beforeEnter: (to, from, next) => {
                if (featureContributionsAvailable()) {
                    next();
                } else {
                    next({ name: 'not-available-international' });
                }
            }
        },
        {
            path: '/cagnotte-en-ligne',
            component: () => import(/* webpackChunkName: "mi-hucha-crear" */ '../modules/hucha/HuchaCrear.vue'),
            name: 'cagnotte-de-naissance',
            meta: { private: false, footerLang: 'fr-FR', headerLang: 'fr-FR', lang: 'fr-FR' },
            beforeEnter: (to, from, next) => {
                if (featureContributionsAvailable()) {
                    next();
                } else {
                    next({ name: 'not-available-international' });
                }
            }
        },
        {
            path: '/salvadanaio-digitale',
            component: () => import(/* webpackChunkName: "mi-hucha-crear" */ '../modules/hucha/HuchaCrear.vue'),
            name: 'salvadanaio-di-nascita',
            meta: { private: false, footerLang: 'it-IT', headerLang: 'it-IT', lang: 'it-IT' },
            beforeEnter: (to, from, next) => {
                if (featureContributionsAvailable()) {
                    next();
                } else {
                    next({ name: 'not-available-international' });
                }
            }
        },
        {
            path: '/online-piggy-bank',
            component: () => import(/* webpackChunkName: "mi-hucha-crear" */ '../modules/hucha/HuchaCrear.vue'),
            name: 'online-piggy-bank',
            meta: { private: false, footerLang: 'en-GB', headerLang: 'en-GB', lang: 'en-GB' },
            beforeEnter: (to, from, next) => {
                if (featureContributionsAvailable()) {
                    next();
                } else {
                    next({ name: 'not-available-international' });
                }
            }
        },
        {
            path: "/digitales-sparschwein",
            component: () => import(/* webpackChunkName: "mi-hucha-crear" */ '../modules/hucha/HuchaCrear.vue'),
            name: "digitales-sparschwein",
            meta: { private: false, footerLang: 'de-DE', headerLang: 'de-DE', lang: 'de-DE' },
            beforeEnter: (to, from, next) => {
                if (featureContributionsAvailable()) {
                    next();
                } else {
                    next({ name: 'not-available-international' });
                }
            }
        },
        {
            path: "/digitale-spaarpot",
            component: () => import(/* webpackChunkName: "mi-hucha-crear" */ '../modules/hucha/HuchaCrear.vue'),
            name: "digitale-spaarpot",
            meta: { private: false, footerLang: 'nl-NL', headerLang: 'nl-NL', lang: 'nl-NL' },
            beforeEnter: (to, from, next) => {
                if (featureContributionsAvailable()) {
                    next();
                } else {
                    next({ name: 'not-available-international' });
                }
            }
        },
        {
            path: "/cofrinho-digital",
            component: () => import(/* webpackChunkName: "mi-hucha-crear" */ '../modules/hucha/HuchaCrear.vue'),
            name: "cofrinho-digital",
            meta: { private: false, footerLang: 'pt-BR', headerLang: 'pt-BR', lang: 'pt-BR' },
            beforeEnter: (to, from, next) => {
                if (featureContributionsAvailable()) {
                    next();
                } else {
                    next({ name: 'not-available-international' });
                }
            }
        },
        {
            path: '/mi-hucha/',
            component: () => import(/* webpackChunkName: "mi-hucha" */ '../modules/hucha/Hucha.vue'),
            name: 'hucha',
            meta: { private: true, footer: 'FooterList' },
            beforeEnter: (to, from, next) => {
                //console.log('store.getters.featureContributionsAvailable = ' + store.getters.featureContributionsAvailable);
                if (featureContributionsAvailable()) {
                    next();
                } else {
                    next({ name: 'not-available-international' });
                }
            }
        },
        // productId -> En el cas dels productes de catàleg serà el listProductId, per poder treballar amb el tema de variants/opcions (el id de producte en variants es compartit). Cas de productes free serà el id de producte
        { path: '/guest/:id/product/:productId/:productType', component: () => import(/* webpackChunkName: "guestProduct" */ '../modules/regalador/ListProduct.vue'), name: 'guestProduct', meta: { header: 'HeaderList', footer: 'FooterList', useListLanguage: true } },
        //{ path: '/guest/:id/onboarded', component: () => import(/* webpackChunkName: "guest" */ '../modules/regalador/List.vue'), name: 'guest', meta: { header: 'HeaderList', footer: 'FooterList' } },
        { path: '/en-camino/:id/:productId/:productType', component: () => import(/* webpackChunkName: "enCamino" */ '../modules/regalador/ListEnCamino.vue'), name: 'enCamino', meta: { header: 'HeaderList', footer: 'FooterList', useListLanguage: true } },
        { path: '/catalogo', component: () => import(/* webpackChunkName: "catalogue" */ '../modules/catalogue/CataloguePage.vue'), name: 'catalogue', meta: { header: 'HeaderCatalogo', headerType: 'baby' } },

        { path: '/categoria/:slug', component: () => import(/* webpackChunkName: "category" */ '../modules/category/CategoryPage.vue'), name: 'category', meta: { header: 'HeaderCatalogo', headerType: 'baby' } },

        { path: '/resultados', component: () => import(/* webpackChunkName: "search" */ '../modules/product/SearchPage.vue'), name: 'search', meta: { header: 'HeaderCatalogo' } },
        { path: '/basesconcurso', component: () => import(/* webpackChunkName: "basesconcurso" */ '../modules/concurso/bases.vue'), name: 'basesconcurso' },
        {
            path: '/onboarding-app',
            name: 'onboardingApp',
            beforeEnter: (to, from, next) => {
                next({ name: 'onboarding0' });
            },
            meta: { private: true }
        },

        { path: '/onboarding/:listType', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingMultiLista.vue'), name: 'OnboardingMultiLista', meta: { private: true, footer: false, header: false, isNewUser: true } },
        { path: '/new-list/:listType', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingMultiLista.vue'), name: 'NewListType', meta: { private: true, footer: 'FooterList', isNewUser: false } },
        { path: '/new-list', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingStep0.vue'), name: 'NewList', meta: { private: true, footer: 'FooterList', isNewUser: false } },

        ...(() => {
            // Normal onboarding routes.
            const steps = [
                { path: '/onboarding-0', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingStep0.vue'), name: 'onboarding0', meta: { private: true, footer: false, header: false } },
                { path: '/onboarding-1', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingStep1.vue'), name: 'onboarding1', meta: { private: true, footer: false, header: false } },
                { path: '/onboarding-2', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingStep2.vue'), name: 'onboarding2', meta: { private: true, footer: false, header: false } },
                { path: '/onboarding-3', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingStepSurvey.vue'), name: 'onboardingSurvey', meta: { private: true, footer: false, header: false } },
                { path: '/onboarding-colaborador', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingStepColaborador.vue'), name: 'onboardingColaborador', meta: { private: true, footer: false, header: false } },
                { path: '/onboarding-invitacion-enviada', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingStepInvitacionEnviada.vue'), name: 'OnboardingStepInvitacionEnviada', meta: { private: true, footer: false, header: false } },
                { path: '/onboarding-puedes-empezar', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingStepPuedesEmpezar.vue'), name: 'OnboardingStepPuedesEmpezar', meta: { private: true, footer: false, header: false } },
                { path: '/onboarding-como-nos-has-encontrado', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingStepHowDidYouFindUs.vue'), name: 'OnboardingStepHowDidYouFindUs', meta: { private: true, footer: false, header: false } },
                { path: '/onboarding-motivo-registro', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingStepWhyDidYouRegister.vue'), name: 'OnboardingStepWhyDidYouRegister', meta: { private: true, footer: false, header: false } },
                { path: '/onboarding-list-template', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingStepListTemplate.vue'), name: 'OnboardingStepListTemplate', meta: { private: true, footer: false, header: false } },
                { path: '/onboarding-ecommerce-birthdate', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingStepEcommerceBirthdate.vue'), name: 'OnboardingStepEcommerceBirthdate', meta: { private: true, footer: false, header: false } },
                { path: '/onboarding-date', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingStepDate.vue'), name: 'onboarding-step-date', meta: { private: true, footer: false, header: false } },
                { path: '/onboarding-name', component: () => import(/* webpackChunkName: "signupOnboarding" */ '../modules/onboarding/OnboardingStepName.vue'), name: 'onboarding-step-name', meta: { private: true, footer: false, header: false } },
            ];

            // Build the routes for the multi-list onboarding.
            const multiListSteps = steps.map((step) => ({
                ...step,

                path: `/onboarding/:listType${step.path}`,
                name: `MultiList${step.name}`,
                meta: {
                    ...step.meta,

                    isNewUser: true,
                    isMultiListStep: true,
                },
            }));

            // Build the routes for the multi-list onboarding.
            const createListSteps = steps.map((step) => ({
                ...step,

                path: `/new-list/:listType${step.path}`,
                name: `NewList${step.name}`,
                meta: {
                    ...step.meta,
                    header: undefined,
                    footer: 'FooterList',

                    isNewUser: false,
                    isMultiListStep: true,
                },
            }));

            return [
                ...steps,
                ...multiListSteps,
                ...createListSteps,
            ];
        })(),

        { path: '/guest/:id', component: () => import(/* webpackChunkName: "guest" */ '../modules/regalador/List.vue'), name: 'guest', meta: { header: 'HeaderList', footer: 'FooterList', useListLanguage: true } },
        { path: '/guest/:id/1', component: () => import(/* webpackChunkName: "OnboardingGuestStep1" */ '../modules/onboarding-guest/OnboardingGuestStep1.vue'), name: 'OnboardingGuestStep1', meta: { footer: false, header: 'HeaderOnboardingGuest', useListLanguage: true } },
        { path: '/guest/:id/2', component: () => import(/* webpackChunkName: "OnboardingGuestStep2" */ '../modules/onboarding-guest/OnboardingGuestStep2.vue'), name: 'OnboardingGuestStep2', meta: { footer: false, header: 'HeaderOnboardingGuest', useListLanguage: true } },
        { path: '/guest/:id/3', component: () => import(/* webpackChunkName: "OnboardingGuestStep3" */ '../modules/onboarding-guest/OnboardingGuestStep3.vue'), name: 'OnboardingGuestStep3', meta: { footer: false, header: 'HeaderOnboardingGuest', useListLanguage: true } },
        { path: '/guest-ads/:id', component: () => import(/* webpackChunkName: "ListAds" */ '../modules/regalador/ListAds.vue'), name: 'ListAds', meta: { header: 'HeaderList', footer: 'FooterList', useListLanguage: true } },
        { path: '/guest-ads-2/:id', component: () => import(/* webpackChunkName: "ListAds2" */ '../modules/regalador/ListAds2.vue'), name: 'ListAds2', meta: { header: 'HeaderList', footer: 'FooterList', useListLanguage: true } },

        { path: '/blog', component: () => import(/* webpackChunkName: "Blog" */ '../modules/blog/Blog.vue'), name: 'Blog', meta: { header: 'HeaderBlog' } },
        { path: '/blog/posts/:slug', component: () => import(/* webpackChunkName: "BlogArticle" */ '../modules/blog/BlogArticle.vue'), name: 'BlogArticle', meta: { header: 'HeaderBlog' } },
        { path: '/blog/categorias/:slug', component: () => import(/* webpackChunkName: "BlogCategory" */ '../modules/blog/BlogCategory.vue'), name: 'BlogCategory', meta: { header: 'HeaderBlog' } },

        { path: '/blog/autor/antonella', component: () => import(/* webpackChunkName: "AntonellaAutor" */ '../modules/blog/AntonellaAutor.vue'), name: 'AntonellaAutor', meta: { header: 'HeaderBlog' } },


        { path: '/seleccion/:slug/:id', component: () => import(/* webpackChunkName: "collection" */ '../modules/collections/CollectionPage.vue'), name: 'collection', meta: { header: 'HeaderCatalogo' } },

        // Només URL acabades en numero https://router.vuejs.org/guide/essentials/route-matching-syntax.html#custom-regex-in-params
        { path: '/:brand/:id(\\d+)', component: () => import(/* webpackChunkName: "product" */ '../modules/product/ProductPage.vue'), name: 'product' },
        { path: '/catalogo/:name/:id(\\d+)', component: () => import(/* webpackChunkName: "product" */ '../modules/product/ProductPage.vue'), name: 'product-new' },

        // landings
        { path: '/landing/app', component: () => import(/* webpackChunkName: "landing-app" */ '../modules/login/LandingApp.vue'), name: 'landing-app', meta: { footer: false, header: false } },
        { path: '/landing/babyshower', component: () => import(/* webpackChunkName: "landing-1" */ '../modules/landings/LandingBabyShower.vue'), name: 'landing-1', meta: { header: false, footer: false } },
        { path: '/landing/listabebe', component: () => import(/* webpackChunkName: "landing-2" */ '../modules/landings/LandingNoList.vue'), name: 'landing-2', meta: { header: false, footer: false } },
        { path: '/landing/listabebe-v1', component: () => import(/* webpackChunkName: "landing-2-v2" */ '../modules/landings/LandingNoList-V2.vue'), name: 'landing-2-v2', meta: { header: false, footer: false } },
        { path: '/landing/liste-bebe', component: () => import(/* webpackChunkName: "landing-2" */ '../modules/landings/LandingNoListFR.vue'), name: 'landing-2-fr', meta: { header: false, footer: false } },
        { path: '/landing/liste-de-naissance', component: () => import(/* webpackChunkName: "landing-2" */ '../modules/landings/LandingNoListFR.vue'), name: 'landing-2-fr-v2', meta: { header: false, footer: false } },
        { path: '/landing/liste-de-naissance-v1', component: () => import(/* webpackChunkName: "landing-2" */ '../modules/landings/LandingNoListFR-V3.vue'), name: 'landing-2-fr-v3', meta: { header: false, footer: false } },
        { path: '/landing/lista-bambino', component: () => import(/* webpackChunkName: "landing-2" */ '../modules/landings/LandingNoListIT.vue'), name: 'landing-2-it', meta: { header: false, footer: false } },
        { path: '/landing/lista-nascita', component: () => import(/* webpackChunkName: "landing-2" */ '../modules/landings/LandingNoListIT.vue'), name: 'landing-2-it-v2', meta: { header: false, footer: false } },
        { path: '/landing/geboortelijst', component: () => import(/* webpackChunkName: "landing-2" */ '../modules/landings/LandingNoListNL.vue'), name: 'landing-baby-nl', meta: { header: false, footer: false } },
        { path: '/landing/geburtsliste', component: () => import(/* webpackChunkName: "landing-baby-registrt-de" */ '../modules/landings/LandingBabyRegistryDE.vue'), name: 'landing-baby-de', meta: { header: false, footer: false } },
        { path: '/landing/lista-cha-de-bebe', component: () => import(/* webpackChunkName: "landing-cha-de-bebe" */ '../modules/landings/LandingBabyRegistryPT.vue'), name: 'landing-baby-pt', meta: { header: false, footer: false } },
        { path: '/landing/sorteo-hellobb-box', component: () => import(/* webpackChunkName: "landing-3" */ '../modules/landings/LandingSorteo2.vue'), name: 'landing-3', meta: { header: false, footer: false } },
        { path: '/landing/afiliado', component: () => import(/* webpackChunkName: "landing-4" */ '../modules/landings/LandingAfiliado.vue'), name: 'landing-4', meta: { header: false, footer: false } },
        { path: '/landing/listabebe-video', component: () => import(/* webpackChunkName: "landing-5" */ '../modules/landings/LandingNoListVideo.vue'), name: 'landing-5', meta: { header: false, footer: false } },
        { path: '/landing/lista-boda', component: () => import(/* webpackChunkName: "lista-de-bodas" */ '../modules/landings/LandingWeddingES.vue'), name: 'landing-wedding-es', meta: { header: false, footer: false } },
        { path: '/landing/liste-mariage', component: () => import(/* webpackChunkName: "liste-mariage" */ '../modules/landings/LandingWeddingFR.vue'), name: 'landing-wedding-fr', meta: { header: false, footer: false } },
        { path: '/landing/lista-nozze', component: () => import(/* webpackChunkName: "lista-nozze" */ '../modules/landings/LandingWeddingIT.vue'), name: 'landing-wedding-it', meta: { header: false, footer: false } },
        { path: '/landing/lista-de-deseos', component: () => import(/* webpackChunkName: "lista-de-deseos" */ '../modules/landings/LandingWishlistES.vue'), name: 'lista-de-deseos-es', meta: { header: false, footer: false } },
        { path: '/landing/verlanglijst', component: () => import(/* webpackChunkName: "lista-de-deseos" */ '../modules/landings/LandingWishlistNL.vue'), name: 'lista-de-deseos-nl', meta: { header: false, footer: false } },
        { path: '/landing/wunschliste', component: () => import(/* webpackChunkName: "lista-de-deseos-de" */ '../modules/landings/LandingWishlistDE.vue'), name: 'lista-de-deseos-de', meta: { header: false, footer: false } },
        { path: '/landing/liste-de-cadeaux', component: () => import(/* webpackChunkName: "liste-de-cadeaux" */ '../modules/landings/LandingWishlistFR.vue'), name: 'lista-de-deseos-fr', meta: { header: false, footer: false } },
        { path: '/landing/liste-de-cadeaux-v1', component: () => import(/* webpackChunkName: "liste-de-cadeaux-v2" */ '../modules/landings/LandingWishlistFR-V2.vue'), name: 'lista-de-deseos-fr-v2', meta: { header: false, footer: false } },
        { path: '/landing/lista-regalo', component: () => import(/* webpackChunkName: "lista-regalo" */ '../modules/landings/LandingWishlistIT.vue'), name: 'lista-de-deseos-it', meta: { header: false, footer: false } },
        { path: '/landing/lista-regalo-v1', component: () => import(/* webpackChunkName: "lista-regalo-v2" */ '../modules/landings/LandingWishlistIT-V2.vue'), name: 'lista-de-deseos-it-v2', meta: { header: false, footer: false } },
        { path: '/landing/liste-anniversaire', component: () => import(/* webpackChunkName: "liste-anniversaire" */ '../modules/landings/LandingBirthdayFR.vue'), name: 'landing-anniversaire-fr', meta: { header: false, footer: false } },
        { path: '/landing/verjaardagslijst', component: () => import(/* webpackChunkName: "list-birthday-nl" */ '../modules/landings/LandingBirthdayNL.vue'), name: 'landing-birthday-nl', meta: { header: false, footer: false } },
        { path: '/landing/geburtstagsliste', component: () => import(/* webpackChunkName: "list-birthday-de" */ '../modules/landings/LandingBirthdayDE.vue'), name: 'landing-birthday-de', meta: { header: false, footer: false } },
        { path: '/landing/lista-regalos-navidad', component: () => import(/* webpackChunkName: "lista-regalos-navidad" */ '../modules/landings/LandingChristmasES.vue'), name: 'landing-regalos-navidad', meta: { header: false, footer: false } },
        { path: '/landing/liste-noel', component: () => import(/* webpackChunkName: "liste-noel" */ '../modules/landings/LandingChristmasFR.vue'), name: 'landing-noel-fr', meta: { header: false, footer: false } },
        { path: '/landing/lista-di-natale', component: () => import(/* webpackChunkName: "lista-di-natale" */ '../modules/landings/LandingChristmasIT.vue'), name: 'landing-di-natale-it', meta: { header: false, footer: false } },
        { path: '/landing/liste-pendaison-cremaillere', component: () => import(/* webpackChunkName: "liste-pendaison-cremaillere" */ '../modules/landings/LandingHomeFR.vue'), name: 'landing-home-fr', meta: { header: false, footer: false } },
        { path: '/landing/lista-comunion', component: () => import(/* webpackChunkName: "lista-comunion" */ '../modules/landings/LandingComunionES.vue'), name: 'landing-comunion-es', meta: { header: false, footer: false } },
        { path: '/landing/lista-regalo-compleanno', component: () => import(/* webpackChunkName: "lista-regalo-compleanno" */ '../modules/landings/LandingBirthdayIT.vue'), name: 'landing-birthday-it', meta: { header: false, footer: false } },
        { path: '/landing/premium-free', component: () => import(/* webpackChunkName: "landing-premium-free" */ '../modules/landings/LandingFreePremium.vue'), name: 'landing-premium-free', meta: { header: false, footer: false } },

        // landings-internacional
        { path: '/landing/mesa-regalos', component: () => import(/* webpackChunkName: "landing-6" */ '../modules/landings/LandingINTMesaRegalos.vue'), name: 'landing-6', meta: { header: false, footer: false, video: false } },
        { path: '/landing/mesa-regalos-video', component: () => import(/* webpackChunkName: "landing-6" */ '../modules/landings/LandingINTMesaRegalos.vue'), name: 'landing-6-video', meta: { header: false, footer: false } },
        { path: '/landing/mesa-babyshower', component: () => import(/* webpackChunkName: "landing-int-babyshower" */ '../modules/landings/LandingINTBabyShower.vue'), name: 'landing-int-babyshower', meta: { header: false, footer: false, video: false } },

        // Vendedores
        { path: '/hellobb-para-vendedores', component: () => import(/* webpackChunkName: "vendedores" */ '../modules/vendedores/vendedores.vue'), name: 'vendedores', meta: { header: 'HeaderCatalogo' } },
        { path: '/informacion-solicitada', component: () => import(/* webpackChunkName: "vendedores" */ '../modules/vendedores/Thanks.vue'), name: 'vendedoresThanks', meta: { header: 'HeaderCatalogo' } },

        {path: '/unsubscribe', component: () => import(/* webpackChunkName: "unsubscribe" */ '../modules/pages/UnsubscribeBridge.vue'), name: 'unsubscribe'},
        {path: '/subscribe', component: () => import(/* webpackChunkName: "subscribe" */ '../modules/pages/SubscribeBridge.vue'), name: 'subscribe'},
        { path: '/error', component: () => import(/* webpackChunkName: "error" */ '../modules/Error'), name: 'error' },
        { path: '/pagina-no-encontrada', component: () => import(/* webpackChunkName: "notfound" */ '../modules/NotFoundPage'), name: 'notfound' },
        { path: '/hasta-pronto', component: () => import(/* webpackChunkName: "seeyousoon" */ '../modules/SeeYouSoon'), name: 'seeyousoon' },
        { path: '*', component: () => import(/* webpackChunkName: "catchall" */ '../modules/NotFoundPage') },
        { path: '/link', component: () => import(/* webpackChunkName: "redirect" */ '../components/Redirect'), name: 'link' },
        { path: '/convert-christmas-list-to-wishlist', component: () => import(/* webpackChunkName: "convert-christmas-list-to-wishlist" */ '../modules/procs/ConvertToWishlist.vue'), name: 'convert-christmas-list-to-wishlist', meta: { private: true, header: false, footer: false } },
    ];

    return originalRoutes
        .filter(item => !!item)
        .map(route => translateRoute(route));
};

// Evitar warnings continuos a la console del servidor: "NavigationDuplicated: Avoided redundant navigation to current location"
const originalPush = Router.prototype.push
Router.prototype.push = function push(location, onResolve, onReject) {
    if (onResolve || onReject) {
        return originalPush.call(this, location, onResolve, onReject)
    }
    return originalPush.call(this, location).catch((err) => {
        console.log('location:');
        if (typeof location === 'string') console.log(location);
        if (typeof location === 'object') console.log(location);
        if (NavigationFailureType) {
            if (Router.isNavigationFailure(err, NavigationFailureType.duplicated)) {
                console.log('VUE-ROUTER NavigationFailure - Duplicated. Aquest "error"/warning sol ser normal. Es genera al navegar cap a una ruta que ja és la actual/última del historial de navegació de vue-router. Normalment podem ignorar aquest error.');
                return err
            }
            if (Router.isNavigationFailure(err, NavigationFailureType.aborted)) {
                console.log('VUE-ROUTER NavigationFailure - aborted.');
                console.log(err);
                return err
            }
            if (Router.isNavigationFailure(err, NavigationFailureType.cancelled)) {
                console.log('VUE-ROUTER NavigationFailure - cancelled');
                console.log(err);
                return err
            }
            if (Router.isNavigationFailure(err, NavigationFailureType.redirected)) {
                console.log('VUE-ROUTER NavigationFailure -redirected');
                console.log(err);
                return err
            }
        } else {
            if (Router.isNavigationFailure(err)) {
                if (Router.isNavigationFailure(err)) {
                    if (err.name === 'NavigationDuplicated') {
                        console.log('VUE-ROUTER NavigationFailure - Duplicated. Aquest "error"/warning sol ser normal. Es genera al navegar cap a una ruta que ja és la actual/última del historial de navegació de vue-router. Normalment podem ignorar aquest error.');
                        return err
                    }
                    if (err.name === 'NavigationAborted') {
                        console.log('VUE-ROUTER NavigationFailure - aborted.');
                        console.log(err);
                        return err
                    }
                    if (err.name === 'NavigationCancelled') {
                        console.log('VUE-ROUTER NavigationFailure - cancelled');
                        console.log(err);
                        return err
                    }
                    if (err.name === 'NavigationRedirected') {
                        console.log('VUE-ROUTER NavigationFailure -redirected');
                        console.log(err);
                        return err
                    }
                }
            }
        }

        return Promise.reject(err)
    })
}

export function getAllRoutes(store, i18n, translatePath = false) {
    /**
     * Receives a RouteConfig object, and returns a new RouteConfig object that
     * contains additional information that can be used to translate routes.
     *
     * @param {vue-router:RouteConfig} routeConfig `RouteConfig` object from
     *     `vue-router` library.
     * @returns {vue-router:RouteConfig} `RouteConfig` object with additional
     *     information.
     * @see {@link https://v3.router.vuejs.org/api/#routes}
     */
    function translateRoute(routeConfig) {
        return routeConfig
        const routeName = routeConfig.name;
        if (!routeName) {
            // This shouldn't happen, but if we are passed something that
            // doesn't have a `name` property, return the received value
            // unchanged.
            return routeConfig;
        }

        // Build array with all the aliases that were defined for this route.
        //
        // If the route had a single alias as a string, this will be an array
        // with a single item.
        let existingAliases = routeConfig.alias || [];
        if (typeof existingAliases === "string") {
            existingAliases = [existingAliases];
        }

        // `finalPath` will be the returned value of the `path` property of the
        // route.
        //
        // It will only be change dwhen the `translatePath` flag is set to
        // `true`.
        let finalPath = routeConfig.path;
        if (translatePath) {
            // Try to get a translated path pattern.
            //
            // If no translation is found, fallback to the original (unchanged)
            // value.
            finalPath = getPreferredRoutePath(i18n, routeConfig.name) || routeConfig.path;
        }

        // Build an array that contains:
        //
        //     - The original route aliases; and
        //     - One alias per translated route.
        //
        // See:
        //
        //     https://v3.router.vuejs.org/guide/essentials/redirect-and-alias.html#alias
        const aliases = [
            ...existingAliases,
            ...getRouteAliases(i18n, routeName)
        ]
            // Remove duplicates.
            .filter(unique)
            // If the route's `path` property appears as an alias, remove it.
            .filter((alias) => alias !== finalPath);

        // This is the modified route object.
        const finalRoute = {
            // Copy all the original data.
            ...routeConfig,

            // `finalPath` can be either the original route path, or a
            // translated path.
            path: finalPath,

            meta: {
                // Copy the original value of `meta`.
                ...(routeConfig.meta || {}),

                // For some reason we can't read `to.alias` from the
                // `router.beforeEach` callback,
                aliases,
            },

            // `aliases` contains the original aliases (if any), plus an alias
            // for each possible route translation.
            //
            // See:
            //
            //     https://v3.router.vuejs.org/guide/essentials/redirect-and-alias.html#alias
            alias: aliases,
        };

        return finalRoute;
    }

    return routes({
        translateRoute,

        featureContributionsAvailable: () => featureContributionsAvailable(store.state),
        locationCountry: () => countryCode(store.state),
        isAuthenticated: () => isAuthenticated(store.state),
    });
}

export function createRouter(store, i18n) {
    const router = new Router({
        routes: getAllRoutes(store, i18n),
        mode: 'history',
        scrollBehavior(to, from, savedPosition) {
            let offset = { x: 0, y: 0 }
            const stickyHeaderOffset = { x: 0, y: 90 } // A ull! quina és l'alçada del sticky header     // TODO Sergi -> No se com utilitzar REMS en comptes de pixels?
            if (to.name === 'faq') {
                offset = stickyHeaderOffset
            }

            let position
            if (savedPosition) {
                position = savedPosition // només té valor savedPosition si és navegació a través botons back/next del navegador
                const routesWaitCanScroll = ['catalogue', 'category', 'product', 'constructor', 'list-constructor','owner-list']
                if (routesWaitCanScroll.includes(to.name)) {
                    return new Promise(resolve => {
                        const unsubscribeFn = this.app.$store.subscribe(mutation => {
                            if (mutation.type === "setCanScroll") {
                                unsubscribeFn();
                                resolve(position);
                            }
                        });
                    });
                }
            }
            else if (to.hash) {
                position = { selector: to.hash, offset }
                //position = { selector: to.hash }
                return new Promise(resolve => {
                    const unsubscribeFn = this.app.$store.subscribe(mutation => {
                        if (mutation.type === "setCanScroll") {
                            unsubscribeFn();
                            resolve(position);
                        }
                    });
                });
            } else if (to.name === from.name) {
                const routesScrollTotop = ['product', 'category']
                if (routesScrollTotop.includes(to.name)) {
                    // Productes relacionats (product) té link que porta a la mateixa pàgina però scroll es queda a sota i el volem adalt
                    // idem per a categories i subcategories
                    position = { x: 0, y: 0 }
                } else {
                    position = false // mantenir scroll si la pàgina és la mateixa
                }
            } else {
                position = { x: 0, y: 0 }
            }
            return position;
        }
    });

    router.beforeEach(async (to, from, next) => {
        console.log(from.name + " --> " + to.name);
        // Required to properly detect some changes in state.
        //
        // For example, when a user without products adds their own product.
        if (router.app.$store) {
            console.log('router.app.$forceRefreshState()');
            router.app.$forceRefreshState();

            // Ensure `i18n.locale` is up-to-date.
            //i18n.locale = router.app.$store.getters.preferredLanguage || 'es';
            i18n.locale = store?.getters?.preferredLanguage || router.app.$store.getters.preferredLanguage || "es";

            // Guest lists can have their own language if the user overrides
            // them with a language selector.
            //
            // Without this code, when the user is in the guest list, and
            // navigates to a specific product, the language is temporarily
            // overriden to the "normal" guest language (e.g. by geolocation),
            // instead of the manually-chosen language.
            if (to.meta?.useListLanguage) {
                i18n.locale = store?.getters?.myListLanguage || router.app.$store.getters.myListLanguage || i18n.locale;
            }
        }

        if (typeof window !== 'undefined') {
            if (typeof document !== 'undefined') {
                // TODO Hack a eliminar si hi ha encara algún modal antic que no es tanca correctament i bloqueja l'scroll de pàgina
                document.body.style.overflowY = "";
            }
        }

        if (to.meta.private && !store.state.isAuthenticated) {
            // if (window.MauiApp?.OnSessionLost) {
            //     window.MauiApp.OnSessionLost();
            // }
            if (to.meta.iframe) {
                next({
                    name: 'external-ecommerce-login-iframe',
                    params: {
                        wantedRoute: to.fullPath,
                    },
                    query: {
                        wantedRoute: to.fullPath,
                    },
                });

                return;
            }

            next({
                name: 'login',
                query: { redirect: to.fullPath },
                params: {
                    wantedRoute: to.fullPath,
                },
            })

            return;
        }

        const hasAliases = to?.meta?.aliases && to.meta.aliases.length > 0;
        if (hasAliases && typeof window !== "undefined") {
            const translatedPath = getPreferredRoutePath(i18n, to.name);

            // Create a new router but only use it as a temporary variable.
            const secondRouter = new Router({
                // Pass `true` to `getAllRoutes` because we want `path` to be
                // the translated route.
                routes: getAllRoutes(store, i18n, true),

                mode: "history",
            });

            // Ask the temporary router to resolve the translated path.
            //
            // This will parse the received navigation object and return
            // results of how the router parsed it.
            const expectedResolvedRoute = secondRouter.resolve({
                ...to,

                path: translatedPath,
            });

            // Use the resolution results from the second router to obtain a
            // path that is both:
            //
            //     - Translated; and
            //     - has all params replaced (i.e. has real values instead of
            //       placeholders like `:id`).
            const expectedPath = expectedResolvedRoute.route.fullPath;

            // If the route we're going to
            if (to.path !== expectedPath) {
                next({
                    path: expectedPath,

                    hash: to.hash,
                    params: to.params,
                    query: to.query,
                });

                return;
            }
        }

        try {
            next()
        } catch (error) {
            console.log(error)
            console.log(error.name)
        }
    });

    return router;
}
